import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Phone, CalendarPlus, Flame, RefreshCw, MailWarning, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ENGAGE = {
  fantasma: { label: "Fantasma", cls: "bg-[#94A3B8] hover:bg-[#94A3B8]" },
  curioso: { label: "Curioso", cls: "bg-[#F59E0B] hover:bg-[#F59E0B]" },
  enganchado: { label: "Enganchado", cls: "bg-[#10B981] hover:bg-[#10B981]" },
};
const ESTADO_CLS = { TRIAL: "border-[#0E2A47] text-[#0E2A47]", EXPIRADO: "border-[#EF4444] text-[#EF4444]", PAGADO: "border-[#10B981] text-[#10B981]" };

function Counter({ label, value, accent }) {
  return (
    <div className={`rounded-xl border p-4 ${accent ? "border-[#FF5625] bg-[#FF5625]/5" : "border-[#E2E8F0] bg-white"}`}>
      <div className="text-3xl font-black text-[#0E2A47]">{value}</div>
      <div className="text-xs uppercase tracking-wider text-[#0E2A47]/60 mt-1">{label}</div>
    </div>
  );
}

export default function TrialsTab() {
  const [data, setData] = useState({ tenants: [], contadores: {} });
  const [loading, setLoading] = useState(true);
  const [fEng, setFEng] = useState("todos");
  const [fNorma, setFNorma] = useState("todos");
  const [fEstado, setFEstado] = useState("todos");
  const [fDias, setFDias] = useState("");
  const [sortKey, setSortKey] = useState("dias_restantes");
  const [sortDir, setSortDir] = useState("asc");
  const [contactDlg, setContactDlg] = useState(null);
  const [nota, setNota] = useState("");
  const [rescateDlg, setRescateDlg] = useState(null);
  const [rescateBusy, setRescateBusy] = useState(false);

  const abrirRescate = async () => {
    try {
      const { data } = await api.get("/admin/trials/fantasmas");
      setRescateDlg(data);
    } catch { toast.error("No se pudo cargar la lista de fantasmas"); }
  };
  const confirmarRescate = async () => {
    setRescateBusy(true);
    try {
      const { data } = await api.post("/admin/trials/rescate-fantasmas");
      toast.success(`Rescate enviado a ${data.enviados} · ${data.saltados} saltados`);
      setRescateDlg(null); cargar();
    } catch { toast.error("No se pudo enviar el rescate"); }
    finally { setRescateBusy(false); }
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/trials/panel"); setData(data); }
    catch { toast.error("No se pudo cargar el panel de trials"); }
    setLoading(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const extender = async (t) => {
    try {
      await api.post(`/admin/trials/${t.id}/extender`, { dias: 7, motivo: "Extensión manual desde panel" });
      toast.success(`+7 días para ${t.empresa}`);
      cargar();
    } catch { toast.error("No se pudo extender el trial"); }
  };
  const guardarContacto = async () => {
    try {
      await api.post(`/admin/trials/${contactDlg.id}/contactado`, { nota });
      toast.success("Marcado como contactado");
      setContactDlg(null); setNota(""); cargar();
    } catch { toast.error("No se pudo guardar"); }
  };

  const normas = useMemo(() => [...new Set(data.tenants.map((t) => t.norma_activa).filter(Boolean))], [data]);

  const rows = useMemo(() => {
    let r = [...data.tenants];
    if (fEng !== "todos") r = r.filter((x) => x.engagement === fEng);
    if (fNorma !== "todos") r = r.filter((x) => x.norma_activa === fNorma);
    if (fEstado !== "todos") r = r.filter((x) => x.estado === fEstado);
    if (fDias !== "") r = r.filter((x) => x.dias_restantes !== null && x.dias_restantes <= Number(fDias));
    r.sort((a, b) => {
      const va = a[sortKey], vb = b[sortKey];
      const na = va === null || va === undefined ? 9e9 : va;
      const nb = vb === null || vb === undefined ? 9e9 : vb;
      if (na < nb) return sortDir === "asc" ? -1 : 1;
      if (na > nb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return r;
  }, [data, fEng, fNorma, fEstado, fDias, sortKey, sortDir]);

  const th = (key, label) => (
    <th onClick={() => { setSortKey(key); setSortDir(sortKey === key && sortDir === "asc" ? "desc" : "asc"); }}
      className="px-3 py-2 text-left cursor-pointer select-none hover:text-[#FF5625] whitespace-nowrap"
      data-testid={`th-${key}`}>
      {label}{sortKey === key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
    </th>
  );

  const c = data.contadores || {};

  return (
    <div className="space-y-4" data-testid="trials-tab-content">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Counter label="Trials activos" value={c.trials_activos ?? 0} />
        <Counter label="Enganchados" value={c.enganchados ?? 0} accent />
        <Counter label="Expiran esta semana" value={c.expiran_esta_semana ?? 0} />
        <Counter label="Convertidos este mes" value={c.convertidos_este_mes ?? 0} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={fEng} onValueChange={setFEng}><SelectTrigger className="w-40" data-testid="filtro-engagement"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="todos">Todo engagement</SelectItem><SelectItem value="fantasma">Fantasma</SelectItem><SelectItem value="curioso">Curioso</SelectItem><SelectItem value="enganchado">Enganchado</SelectItem></SelectContent></Select>
        <Select value={fNorma} onValueChange={setFNorma}><SelectTrigger className="w-40" data-testid="filtro-norma"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="todos">Toda norma</SelectItem>{normas.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent></Select>
        <Select value={fEstado} onValueChange={setFEstado}><SelectTrigger className="w-36" data-testid="filtro-estado"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="todos">Todo estado</SelectItem><SelectItem value="TRIAL">Trial</SelectItem><SelectItem value="EXPIRADO">Expirado</SelectItem><SelectItem value="PAGADO">Pagado</SelectItem></SelectContent></Select>
        <Input type="number" placeholder="Días restantes ≤" value={fDias} onChange={(e) => setFDias(e.target.value)} className="w-40" data-testid="filtro-dias" />
        <Button variant="outline" size="sm" onClick={cargar} data-testid="trials-refresh"><RefreshCw size={14} className="mr-1" />Actualizar</Button>
        <Button size="sm" onClick={abrirRescate} data-testid="rescate-fantasmas-btn" className="bg-[#FF5625] hover:bg-[#E0451A] text-white"><MailWarning size={14} className="mr-1" />Enviar rescate a fantasmas</Button>
      </div>

      <Card className="border-[#E2E8F0] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F8FA] text-[#0E2A47]/70 font-semibold border-b border-[#E2E8F0]">
            <tr>
              {th("empresa", "Empresa")}{th("norma_activa", "Norma")}{th("estado", "Estado")}
              {th("fecha_alta", "Alta")}{th("dias_restantes", "Días")}{th("engagement", "Engagement")}
              {th("docs_subidos", "Docs")}{th("readiness_pct", "Readiness")}{th("ultimo_acceso", "Últ. acceso")}
              <th className="px-3 py-2 text-left">Contacto</th><th className="px-3 py-2 text-left">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => {
              const llamar = t.llamar_hoy;
              const eng = ENGAGE[t.engagement] || ENGAGE.fantasma;
              return (
                <tr key={t.id} data-testid={`trial-row-${t.id}`}
                  className={`border-b border-[#F1F5F9] ${llamar ? "bg-[#10B981]/10" : ""}`}>
                  <td className="px-3 py-2 font-medium text-[#0E2A47]">
                    {t.empresa}
                    {llamar && <Badge className="ml-2 bg-[#10B981] hover:bg-[#10B981]" data-testid={`llamar-hoy-${t.id}`}><Flame size={11} className="mr-1" />LLAMAR HOY</Badge>}
                  </td>
                  <td className="px-3 py-2 text-xs">{t.norma_activa}</td>
                  <td className="px-3 py-2"><Badge variant="outline" className={ESTADO_CLS[t.estado]}>{t.estado}</Badge></td>
                  <td className="px-3 py-2 text-xs">{(t.fecha_alta || "").slice(0, 10)}</td>
                  <td className="px-3 py-2 font-bold">{t.dias_restantes ?? "—"}</td>
                  <td className="px-3 py-2"><Badge className={eng.cls} data-testid={`engagement-${t.id}`}>{eng.label}</Badge></td>
                  <td className="px-3 py-2">{t.docs_subidos}</td>
                  <td className="px-3 py-2">{t.readiness_pct}%</td>
                  <td className="px-3 py-2 text-xs">{(t.ultimo_acceso || "").slice(0, 10) || "—"}</td>
                  <td className="px-3 py-2 text-xs">
                    <div>{t.email || "—"}</div>
                    {t.telefono && <div className="text-[#0E2A47]/60">{t.telefono}</div>}
                    {t.contactado_en && <div className="text-[#10B981]">✓ {t.contactado_en.slice(0, 10)}</div>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <Button size="sm" variant="outline" className="mr-1 h-7 text-xs" onClick={() => extender(t)} data-testid={`extender-${t.id}`}><CalendarPlus size={12} className="mr-1" />+7d</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setContactDlg(t); setNota(t.contacto_nota || ""); }} data-testid={`contactar-${t.id}`}><Phone size={12} className="mr-1" />Contactado</Button>
                  </td>
                </tr>
              );
            })}
            {!loading && rows.length === 0 && <tr><td colSpan={11} className="px-3 py-8 text-center text-[#0E2A47]/50">Sin resultados con estos filtros.</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!contactDlg} onOpenChange={(o) => !o && setContactDlg(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Marcar como contactado · {contactDlg?.empresa}</DialogTitle></DialogHeader>
          <Textarea placeholder="Nota de la llamada (opcional)" value={nota} onChange={(e) => setNota(e.target.value)} data-testid="contacto-nota" rows={4} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setContactDlg(null)}>Cancelar</Button>
            <Button className="bg-[#FF5625] hover:bg-[#E0451A]" onClick={guardarContacto} data-testid="contacto-guardar">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rescateDlg} onOpenChange={(o) => !o && setRescateDlg(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="rescate-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0E2A47]">Enviar rescate "Te lo montamos" a fantasmas</DialogTitle>
            <DialogDescription className="sr-only">Envío manual de la plantilla de rescate a tenants fantasma sin documentos.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-[#0E2A47]/70">
            Se enviará la plantilla 24 a los tenants <b>fantasma</b> (0 documentos) que aún no la han recibido.
            {rescateDlg && <> Se escribirá a <b data-testid="rescate-pendientes">{rescateDlg.pendientes_envio}</b> de {rescateDlg.total} fantasmas ({rescateDlg.total - rescateDlg.pendientes_envio} ya recibieron el email y se saltan).</>}
          </p>
          <div className="max-h-72 overflow-y-auto rounded-lg border border-[#E2E8F0]" data-testid="rescate-lista">
            <table className="w-full text-xs">
              <thead className="bg-[#F7F8FA] text-[#0E2A47]/60 sticky top-0"><tr>
                <th className="px-2 py-1.5 text-left">Empresa</th><th className="px-2 py-1.5 text-left">Email</th>
                <th className="px-2 py-1.5 text-left">Días</th><th className="px-2 py-1.5 text-left">Docs</th><th className="px-2 py-1.5 text-left">Estado</th>
              </tr></thead>
              <tbody>
                {(rescateDlg?.fantasmas || []).map((f) => (
                  <tr key={f.id} className="border-b border-[#F1F5F9]">
                    <td className="px-2 py-1.5 font-medium text-[#0E2A47]">{f.nombre}</td>
                    <td className="px-2 py-1.5 text-[#0E2A47]/70">{f.email || "—"}</td>
                    <td className="px-2 py-1.5">{f.dias_desde_alta ?? "—"}</td>
                    <td className="px-2 py-1.5">{f.documentos}</td>
                    <td className="px-2 py-1.5">{f.ya_enviado
                      ? <span className="text-[#10B981]">✓ enviado</span>
                      : <span className="text-[#FF5625]">pendiente</span>}</td>
                  </tr>
                ))}
                {rescateDlg && rescateDlg.fantasmas.length === 0 && <tr><td colSpan={5} className="px-2 py-6 text-center text-[#0E2A47]/50">No hay fantasmas ahora mismo.</td></tr>}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRescateDlg(null)} data-testid="rescate-cancelar">Cancelar</Button>
            <Button className="bg-[#FF5625] hover:bg-[#E0451A]" disabled={rescateBusy || !rescateDlg?.pendientes_envio}
              onClick={confirmarRescate} data-testid="rescate-confirmar">
              {rescateBusy ? <><Loader2 size={15} className="mr-1 animate-spin" />Enviando…</> : `Enviar a ${rescateDlg?.pendientes_envio ?? 0}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
